/**
 * Calendar CSS custom properties derived from design-system tokens.
 *
 * Maps FILLS / STROKES / RADII / FONTS / GRADIENTS → CSS variable names used
 * in availabilityCalendar.css. Applied as inline style on the calendar root.
 *
 * No hex literals — all values imported from tokens.ts.
 */
import {
  FILLS,
  FONTS,
  GRADIENTS,
  RADII,
  SEARCH_LAYER,
  SIZING,
  STROKES,
} from '../../../styles/tokens';

export interface CalendarThemeVars {
  '--cal-bg': string;
  '--cal-cell-size': string;
  '--cal-cell-radius': string;
  '--cal-color-primary': string;
  '--cal-color-muted': string;
  '--cal-color-light': string;
  '--cal-color-page': string;
  '--cal-range-fill': string;
  '--cal-endpoint-gradient': string;
  '--cal-endpoint-shadow': string;
  '--cal-stroke-subtle': string;
  '--cal-hover-bg': string;
  '--cal-hover-border': string;
  '--cal-today-dot': string;
  '--cal-focus-ring-inner': string;
  '--cal-focus-ring-outer': string;
  '--cal-text-unavailable': string;
  '--cal-font-body': string;
  '--cal-chip-bg': string;
  '--cal-surface-active': string;
}

/**
 * Returns the CSS custom properties object for the Compact Gold calendar theme.
 * Apply via `style={calendarThemeVars}` on the calendar root element.
 */
export const calendarThemeVars: CalendarThemeVars = {
  '--cal-bg': FILLS.surface,
  '--cal-cell-size': SIZING.touchTarget,
  '--cal-cell-radius': RADII.control, // 6px rounded-square
  '--cal-color-primary': FILLS.textPrimary,
  '--cal-color-muted': FILLS.textMuted,
  '--cal-color-light': FILLS.textLight,
  '--cal-color-page': FILLS.page,
  '--cal-range-fill': FILLS.goldAlpha15, // full-bleed connected range wash
  '--cal-endpoint-gradient': GRADIENTS.goldButton, // gradient-gold endpoint pills
  '--cal-endpoint-shadow': `0 2px 10px ${FILLS.goldAlpha30}`,
  '--cal-stroke-subtle': STROKES.subtle,
  '--cal-hover-bg': FILLS.surfaceActive,
  '--cal-hover-border': FILLS.goldAlpha30,
  '--cal-today-dot': FILLS.gold, // small gold today-dot
  '--cal-focus-ring-inner': FILLS.page,
  '--cal-focus-ring-outer': FILLS.gold,
  '--cal-text-unavailable': FILLS.textMuted,
  '--cal-font-body': FONTS.body,
  '--cal-chip-bg': FILLS.chip,
  '--cal-surface-active': FILLS.surfaceActive,
} as const;

/**
 * The calendar on the cream search surface: transparent background so the
 * popover or sheet shows through, ink text steps, a gold-alpha range wash
 * that keeps ink readable, card-fill hover and chips, and the search copper
 * for the today dot and focus ring. The gold endpoint gradient is unchanged.
 */
export const calendarCreamThemeVars: CalendarThemeVars = {
  ...calendarThemeVars,
  '--cal-bg': FILLS.transparent,
  '--cal-color-primary': SEARCH_LAYER.ink,
  '--cal-color-muted': SEARCH_LAYER.inkMuted,
  '--cal-color-light': SEARCH_LAYER.inkSecondary,
  '--cal-color-page': SEARCH_LAYER.ink,
  '--cal-range-fill': FILLS.goldAlpha30,
  '--cal-stroke-subtle': SEARCH_LAYER.stroke,
  '--cal-hover-bg': SEARCH_LAYER.card,
  '--cal-today-dot': SEARCH_LAYER.accent,
  '--cal-focus-ring-inner': SEARCH_LAYER.surface,
  '--cal-focus-ring-outer': SEARCH_LAYER.accent,
  '--cal-text-unavailable': SEARCH_LAYER.inkMuted,
  '--cal-chip-bg': SEARCH_LAYER.card,
  '--cal-surface-active': SEARCH_LAYER.card,
};
